import type { BondOrder } from './editor';
import type {
  AromaticityPropertySet,
  CipPropertySet,
  DrawingDocument,
  PartialChargePropertySet,
  Provenance,
  StereoLigand
} from '../model';

export interface AtomStereoValue {
  readonly parity: 'clockwise' | 'counterclockwise';
  readonly descriptor?: 'R' | 'S';
}

export type BondStereoValue =
  | { readonly type: 'wedge'; readonly style: 'solid' | 'hashed'; readonly fromNodeId: string }
  | {
      readonly type: 'double-bond';
      readonly configuration: 'cis' | 'trans';
      readonly stereoAtomIds: readonly [string, string];
    }
  // B3 (#271): a double bond whose cis/trans is deliberately undrawn -- its two lines rendered crossed.
  | { readonly type: 'crossed-double' }
  // B4 (#273): a dative bond -- drawn as an arrow from the donor to the acceptor.
  | { readonly type: 'dative'; readonly donorNodeId: string; readonly acceptorNodeId: string }
  // #453: which of the two rings sharing a fusion double bond its second line faces, named by an
  // atom belonging to that ring but not the other.
  | { readonly type: 'double-bond-side'; readonly facingAtomId: string }
  // ADR 0022 D5: a wavy single bond -- the configuration at the stereocentre `fromNodeId` is
  // deliberately undefined.
  | { readonly type: 'wavy'; readonly fromNodeId: string }
  // ADR 0022 D4: a hydrogen bond -- dashed, never part of a molecule.
  | { readonly type: 'hydrogen' }
  // ADR 0026 D6: an agostic interaction -- a half-headed arrow from a C–H bond to a metal.
  | { readonly type: 'agostic' };

type Bond = DrawingDocument['structure']['bonds'][string];

/** ADR 0022: a bond the chemistry engines (RDKit, CDK) see -- not a hydrogen bond, and (ADR 0026)
 *  not a hapto bond. */
export function isChemicalBond(bond: Bond | undefined): boolean {
  return bond !== undefined && bond.type !== 'hydrogen' && bond.type !== 'hapto' && bond.type !== 'agostic';
}

/** ADR 0022 D4: a bond that joins its atoms into one molecule -- everything but a hydrogen bond.
 *  A hapto bond does (ADR 0026 D1): ferrocene is one molecule. */
export function joinsMolecule(bond: Bond | undefined): boolean {
  // ADR 0026 D6: nor does an agostic interaction, which is drawn as a hydrogen bond is stored.
  return bond !== undefined && bond.type !== 'hydrogen' && bond.type !== 'agostic';
}

/** ADR 0022 D4: the graph's edges that join molecules -- all but hydrogen bonds. */
export function moleculeEdges(structure: DrawingDocument['structure']): DrawingDocument['structure']['graph']['edges'] {
  return structure.graph.edges.filter((edge) => joinsMolecule(structure.bonds[edge.id]));
}

/** The order a bond is drawn with: a hydrogen bond (order 0) is laid out as a single bond. */
export function drawnBondOrders(document: DrawingDocument): Readonly<Record<string, BondOrder>> {
  return Object.fromEntries(
    Object.entries(document.structure.bonds).map(([id, bond]) => [id, bond.order === 0 ? 1 : bond.order])
  );
}

export const CALCULATED_PROVENANCE: Provenance = {
  source: 'calculated',
  engine: 'RDKit',
  engineVersion: '2025.03.4',
  method: 'client-side perception'
};

export function aromaticityResult(document: DrawingDocument): AromaticityPropertySet | undefined {
  return document.derivedPropertySets.find((result): result is AromaticityPropertySet => result.kind === 'aromaticity');
}

export function cipResult(document: DrawingDocument): CipPropertySet | undefined {
  return document.derivedPropertySets.find((result): result is CipPropertySet => result.kind === 'cip');
}

export function partialChargeResult(document: DrawingDocument): PartialChargePropertySet | undefined {
  return document.derivedPropertySets.find(
    (result): result is PartialChargePropertySet => result.kind === 'partial-charge'
  );
}

export function aromaticAtomIds(document: DrawingDocument): ReadonlySet<string> {
  return new Set(aromaticityResult(document)?.atomIds ?? []);
}

export function aromaticBondIds(document: DrawingDocument): ReadonlySet<string> {
  return new Set(aromaticityResult(document)?.bondIds ?? []);
}

export function atomStereoValues(document: DrawingDocument): Readonly<Record<string, AtomStereoValue>> {
  const descriptors = cipResult(document)?.atomDescriptors ?? {};
  return Object.fromEntries(
    Object.entries(document.structure.stereochemistry.tetrahedral).map(([atomId, stereo]) => [
      atomId,
      {
        parity: stereo.winding,
        ...(descriptors[atomId] ? { descriptor: descriptors[atomId] } : {})
      }
    ])
  );
}

export function bondStereoValues(document: DrawingDocument): Readonly<Record<string, BondStereoValue>> {
  return {
    ...Object.fromEntries(
      Object.entries(document.structure.stereochemistry.doubleBonds).map(([bondId, stereo]) => [
        bondId,
        {
          type: 'double-bond' as const,
          configuration: stereo.relativeConfiguration,
          stereoAtomIds: stereo.referenceAtomIds
        }
      ])
    ),
    ...Object.fromEntries(
      Object.entries(document.depiction.bondStyles).map(([bondId, style]) => [
        bondId,
        style.style === 'crossed-double'
          ? ({ type: 'crossed-double' } as const)
          : style.style === 'double-bond-side'
            ? ({ type: 'double-bond-side' as const, facingAtomId: style.facingAtomId } as const)
            : style.style === 'wavy'
              ? ({ type: 'wavy' as const, fromNodeId: style.fromAtomId } as const)
              : ({
                  type: 'wedge' as const,
                  style: style.style === 'solid-wedge' ? ('solid' as const) : ('hashed' as const),
                  fromNodeId: style.fromAtomId
                } as const)
      ])
    ),
    ...Object.fromEntries(
      document.structure.graph.edges.flatMap((edge): [string, BondStereoValue][] => {
        const bond = document.structure.bonds[edge.id];
        if (bond?.type === 'hydrogen') return [[edge.id, { type: 'hydrogen' }]];
        if (bond?.type === 'agostic') return [[edge.id, { type: 'agostic' }]];
        if (bond?.type !== 'dative' || bond.donorAtomId === undefined) return [];
        const donorNodeId = bond.donorAtomId;
        const acceptorNodeId = donorNodeId === edge.source ? edge.target : edge.source;
        return [[edge.id, { type: 'dative' as const, donorNodeId, acceptorNodeId }]];
      })
    )
  };
}

export function orderedStereoLigands(
  document: DrawingDocument,
  atomId: string
): readonly [StereoLigand, StereoLigand, StereoLigand, StereoLigand] | undefined {
  const neighbors = document.structure.graph.edges.flatMap((edge) =>
    edge.source === atomId ? [edge.target] : edge.target === atomId ? [edge.source] : []
  );
  if (neighbors.length !== 3 && neighbors.length !== 4) return undefined;
  const ligands: StereoLigand[] = neighbors.map((neighbor) => ({ atomId: neighbor }));
  if (ligands.length === 3) ligands.push({ implicitHydrogen: true });
  return ligands as unknown as readonly [StereoLigand, StereoLigand, StereoLigand, StereoLigand];
}

export function withoutChemicalPerception(document: DrawingDocument): DrawingDocument {
  return {
    ...document,
    structure: {
      ...document.structure,
      stereochemistry: { tetrahedral: {}, doubleBonds: {} }
    },
    depiction: { ...document.depiction, bondStyles: {} },
    derivedPropertySets: document.derivedPropertySets.filter(
      (result) => result.kind !== 'aromaticity' && result.kind !== 'cip'
    )
  };
}
